import { ImpPad, ImpSpacer, ImpSpinner } from '@imprint-payments/imprint-ui';
import { useEffect, useState } from 'react';

import { getCardBackImg } from '../../api/api';
import { useContentfulOnboardingSteps } from '../../api/onboarding/contentful';
import {
  OnboardingStep,
  OnboardingStepKey,
} from '../../api/onboarding/onboardingSteps';
import { useAppSelector, useImpState } from '../../app/hooks';
import { CardDesignOption } from '../../app/merchantDataSlice';
import PINEntry from '../../components/pinEntry/PINEntry';
import SDKHeader from '../../components/sdk/SDKHeader';
import {
  emitSDKEvent,
  emitSDKResult,
  SDK_EVENTS,
  showSDKHeader,
  useSDKContext,
} from '../../utils/sdk';
import CardDesignSelectionPage, {
  CardDesignSelectionContent,
} from './CardDesignSelectionPage';
import CopyCvvPage, { CopyCvvContent } from './CopyCvvPage';
import NewCardCongratsPage, {
  NewCardCongratsContent,
} from './NewCardCongratsPage';
import OnboardingCompletePage, {
  OnboardingCompleteContent,
} from './OnboardingCompletePage';

const renderOnboardingPage = (
  step: OnboardingStep,
  pageData: OnboardingPageData,
  isMultiCardUser: boolean,
  onNext: () => void
) => {
  switch (step.key) {
    case OnboardingStepKey.CARD_DESIGN_SELECTION:
      return pageData.cardDesignOptions ? (
        <CardDesignSelectionPage
          content={step.content as CardDesignSelectionContent}
          cardDesignOptions={pageData.cardDesignOptions}
          onNext={onNext}
        />
      ) : undefined;

    case OnboardingStepKey.CONGRATS:
      return (
        <NewCardCongratsPage
          content={step.content as NewCardCongratsContent}
          onNext={onNext}
        />
      );

    case OnboardingStepKey.CREATE_PIN:
      if (isMultiCardUser) return undefined; // Skip create pin for multi-card user
      return (
        <ImpPad padding="16px">
          <PINEntry newPin onComplete={onNext} />
        </ImpPad>
      );

    case OnboardingStepKey.COPY_CVV:
      return (
        <CopyCvvPage content={step.content as CopyCvvContent} onNext={onNext} />
      );

    case OnboardingStepKey.ALL_SET:
      return (
        <OnboardingCompletePage
          content={step.content as OnboardingCompleteContent}
          onNext={onNext}
        />
      );

    default:
      //TODO: Add error log
      return <div>Unknown onboarding step</div>;
  }
};

interface OnboardingPageData {
  cardDesignOptions?: CardDesignOption[];
}

export default function OnboardingFlow() {
  const { merchantData, loginState, dispatch } = useImpState();
  const { steps, loading } = useContentfulOnboardingSteps();
  const [currentIndex, setCurrentIndex] = useState(0);
  const { result } = useSDKContext();
  const currentStep: OnboardingStep = steps[currentIndex];
  const isLastStep = currentIndex === steps.length - 1;

  const cardDesignOptions = useAppSelector(
    (state) => state.consumerInfoSelector.cardOffer.cardDesignOptions
  );

  const onNext = () => {
    if (!isLastStep) {
      setCurrentIndex((i) => i + 1);
    } else {
      emitSDKResult({
        result,
      });
      emitSDKEvent({
        event: SDK_EVENTS.CLOSED,
      });
    }
  };

  const currentPage = renderOnboardingPage(
    currentStep,
    { cardDesignOptions },
    loginState.multiCardUser,
    onNext
  );

  // Prevent navigating back to offer by browser's back button
  useEffect(() => {
    const pushNewEntryToHistory = () => {
      window.history.pushState(null, '', window.location.pathname);
    };
    pushNewEntryToHistory();
    window.addEventListener('popstate', pushNewEntryToHistory);

    return () => {
      window.removeEventListener('popstate', pushNewEntryToHistory);
    };
  }, []);

  useEffect(() => {
    if (!currentPage) {
      onNext();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  useEffect(() => {
    if (steps.some((step) => step.key === OnboardingStepKey.COPY_CVV)) {
      dispatch(getCardBackImg(merchantData.productUUID));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [merchantData.productUUID]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100dvh',
      }}
    >
      <ImpSpacer height="m" />
      {showSDKHeader() && <SDKHeader />}
      {loading ? <ImpSpinner /> : currentPage}
    </div>
  );
}
